import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_ahsga-portal/artifacts/h8agb7mt_ahsga_circle_icon_enhanced.png%201.png';

const Register = () => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (!phone.startsWith('255') || phone.length !== 12 || !/^\d+$/.test(phone)) {
      toast.error('Phone must be in format 255XXXXXXXXX');
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, {
        full_name: fullName,
        email,
        phone,
        password
      });
      
      if (response.status === 201) {
        setSuccess(true);
        toast.success('Registration successful! Please check your email to verify your account.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <img src={LOGO_URL} alt="AHSGA Logo" className="w-16 h-16 object-contain mx-auto" />
              <div className="w-16 h-16 bg-green-100 rounded-full mx-auto flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-serif font-bold text-slate-900">Check Your Email</h2>
              <p className="text-slate-600">
                We&apos;ve sent a verification link to <strong>{email}</strong>
              </p>
              <p className="text-sm text-slate-500">
                Please click the link in the email to verify your account and complete registration.
              </p>
              <Button onClick={() => navigate('/member/login')} className="w-full mt-4">
                Go to Member Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
      </div>
      
      <div className="relative z-10 w-full max-w-md">
        <Card className="bg-white/95 backdrop-blur-md shadow-2xl border border-slate-200">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <img src={LOGO_URL} alt="AHSGA Logo" className="w-16 h-16 object-contain" />
            </div>
            <CardTitle className="text-3xl font-serif font-bold text-slate-900">
              AHSGA Portal
            </CardTitle>
            <p className="text-slate-600 mt-2">Create your account</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  data-testid="fullname-input"
                  type="text"
                  placeholder="John Doe"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  minLength={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  placeholder="you@ahsga.me"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <p className="text-xs text-slate-500">Only @ahsga.me emails are allowed</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  data-testid="phone-input"
                  type="tel"
                  placeholder="255XXXXXXXXX"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  maxLength={12}
                />
                <p className="text-xs text-slate-500">Format: 255XXXXXXXXX (Tanzania)</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="password-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    data-testid="confirm-password-input"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                    data-testid="toggle-confirm-password-visibility"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="submit-button"
                disabled={loading}
                className="w-full bg-slate-900 hover:bg-slate-800"
              >
                {loading ? 'Creating Account...' : 'Register'}
              </Button>
            </form>

            <div className="mt-6 text-center space-y-2">
              <Link
                to="/member/login"
                data-testid="member-login-link"
                className="text-sm text-slate-600 hover:text-slate-900 block"
              >
                Already have an account? Member Login
              </Link>
              <Link
                to="/admin/login"
                className="text-sm text-slate-600 hover:text-slate-900 block"
              >
                Admin? Login here
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-white mt-6 opacity-80">
          © 2026 AHSGA. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default Register;